import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

const PinInput = ({ testid, value, onChange, label }) => (
  <label className="block">
    <span className="label-gold block mb-2">{label}</span>
    <input
      data-testid={testid}
      type="password"
      inputMode="numeric"
      autoComplete="off"
      maxLength={4}
      className="input-navy font-mono tracking-[0.6em] text-center"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
    />
  </label>
);

const TIMEOUTS = [1, 5, 15, 30, 60];

const PinTimeout = () => {
  const { user, mergeUser } = useAuth();
  const { t } = useI18n();
  const choose = async (n) => {
    try {
      const { data } = await api.patch("/auth/preferences", { pin_timeout: n });
      mergeUser(data);
      toast.success(t("set_saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <span className="label-gold block mb-3">{t("pin_timeout")}</span>
      <div className="flex flex-wrap gap-2" data-testid="pin-timeout-picker">
        {TIMEOUTS.map((n) => (
          <button key={n} data-testid={`pin-timeout-${n}`} onClick={() => choose(n)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${user.pin_timeout === n ? "bg-[#E5C158] text-[#060A12] border-[#E5C158]" : "border-white/10 text-[#C2C9DB] hover:border-[#E5C158]/50"}`}>
            {n === 60 ? "1 h" : `${n} min`}
          </button>
        ))}
      </div>
    </div>
  );
};

export const PinSettings = () => {
  const { user, mergeUser } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState(null); // set | change | disable
  const [f, setF] = useState({ current: "", pin: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const close = () => { setMode(null); setF({ current: "", pin: "", confirm: "" }); };

  const submit = async (e) => {
    e.preventDefault();
    if (mode !== "disable" && (f.pin.length !== 4 || f.pin !== f.confirm)) return toast.error(t("pin_mismatch"));
    setBusy(true);
    try {
      const { data } = mode === "disable"
        ? await api.post("/auth/pin/disable", { pin: f.current })
        : await api.post("/auth/pin", { pin: f.pin, current_pin: mode === "change" ? f.current : undefined });
      mergeUser(data);
      toast.success(t(mode === "disable" ? "pin_disabled" : "pin_saved"));
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-[#8E9BB4] max-w-md" data-testid="pin-status">{user.pin_enabled ? t("pin_on_desc").replace("{n}", user.pin_timeout) : t("pin_off_desc")}</p>
          {user.pin_enabled ? (
            <div className="flex gap-3">
              <button data-testid="pin-change-btn" onClick={() => setMode("change")} className="px-4 py-2 rounded-full border border-[#E5C158]/40 text-sm text-[#E5C158] hover:bg-[#E5C158]/10">{t("pin_change")}</button>
              <button data-testid="pin-disable-btn" onClick={() => setMode("disable")} className="px-4 py-2 rounded-full border border-white/10 text-sm text-red-300 hover:border-red-400/50">{t("pin_disable")}</button>
            </div>
          ) : (
            <button data-testid="pin-enable-btn" onClick={() => setMode("set")} className="btn-gold px-5 py-2.5 rounded-full text-sm">{t("pin_enable")}</button>
          )}
        </div>
        {user.pin_enabled && <PinTimeout />}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 fade-up" data-testid="pin-form">
      <div className="grid sm:grid-cols-3 gap-4">
        {mode !== "set" && <PinInput testid="pin-current-input" label={t("pin_current")} value={f.current} onChange={set("current")} />}
        {mode !== "disable" && <PinInput testid="pin-new-input" label={t("pin_new")} value={f.pin} onChange={set("pin")} />}
        {mode !== "disable" && <PinInput testid="pin-confirm-input" label={t("pin_confirm")} value={f.confirm} onChange={set("confirm")} />}
      </div>
      <div className="flex gap-3 justify-end">
        <button type="button" data-testid="pin-cancel-btn" onClick={close} className="px-4 py-2 rounded-full border border-white/10 text-sm text-[#C2C9DB]">{t("d_cancel")}</button>
        <button type="submit" data-testid="pin-submit-btn" disabled={busy} className="btn-gold px-5 py-2 rounded-full text-sm inline-flex items-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} {t(mode === "disable" ? "pin_disable" : "d_save")}
        </button>
      </div>
    </form>
  );
};
